import type { ReleaseKind } from "./musicReleaseTabs";
import { trackIdentityKey } from "./trackIdentity";

export const GRID_PAGE_SIZE = 24;

export type ReleaseSort = "newest" | "oldest" | "name-asc" | "name-desc" | "tracks" | "popular";

export type KindFilter = ReleaseKind | "all";

export const YEAR_ALL = "all";

export interface GridEntry {
  name: string;
  releaseDate: string;
  kind: ReleaseKind;
  totalTracks: number;
  searchText: string;
  popularityRank?: number | null;
}

export interface GridFilters {
  query: string;
  kind: KindFilter;
  year: string;
}

export const DEFAULT_GRID_FILTERS: GridFilters = { query: "", kind: "all", year: YEAR_ALL };

export function releaseTime(date: string | undefined): number {
  if (!date) return 0;
  const time = new Date(date).getTime();
  return Number.isNaN(time) ? 0 : time;
}

export function releaseYear(date: string | undefined): number | null {
  const match = /^(\d{4})/.exec(date || "");
  return match ? Number(match[1]) : null;
}

export function yearOptions(entries: Pick<GridEntry, "releaseDate">[]): number[] {
  const years = new Set<number>();
  for (const entry of entries) {
    const year = releaseYear(entry.releaseDate);
    if (year !== null) years.add(year);
  }
  return [...years].sort((a, b) => b - a);
}

export function kindCounts(entries: Pick<GridEntry, "kind">[]): Record<ReleaseKind, number> {
  const counts: Record<ReleaseKind, number> = { album: 0, ep: 0, single: 0, feature: 0 };
  for (const entry of entries) counts[entry.kind] += 1;
  return counts;
}

export function filterEntries<T extends GridEntry>(entries: T[], filters: GridFilters): T[] {
  const query = filters.query.trim().toLowerCase();
  return entries.filter((entry) => {
    if (filters.kind !== "all" && entry.kind !== filters.kind) return false;
    if (filters.year !== YEAR_ALL && String(releaseYear(entry.releaseDate)) !== filters.year) return false;
    if (query && !entry.searchText.toLowerCase().includes(query)) return false;
    return true;
  });
}

export function sortEntries<T extends GridEntry>(entries: T[], sort: ReleaseSort): T[] {
  const newestFirst = (a: T, b: T) => releaseTime(b.releaseDate) - releaseTime(a.releaseDate) || a.name.localeCompare(b.name);
  const sorted = [...entries];
  sorted.sort((a, b) => {
    switch (sort) {
      case "oldest":
        return releaseTime(a.releaseDate) - releaseTime(b.releaseDate) || a.name.localeCompare(b.name);
      case "name-asc":
        return a.name.localeCompare(b.name);
      case "name-desc":
        return b.name.localeCompare(a.name);
      case "tracks":
        return b.totalTracks - a.totalTracks || newestFirst(a, b);
      case "popular": {
        const rankA = a.popularityRank ?? Number.POSITIVE_INFINITY;
        const rankB = b.popularityRank ?? Number.POSITIVE_INFINITY;
        if (rankA !== rankB) return rankA - rankB;
        return newestFirst(a, b);
      }
      default:
        return newestFirst(a, b);
    }
  });
  return sorted;
}

export function activeFilterCount(filters: GridFilters, sort: ReleaseSort, defaultSort: ReleaseSort = "newest"): number {
  return (
    Number(Boolean(filters.query.trim())) +
    Number(filters.kind !== "all") +
    Number(filters.year !== YEAR_ALL) +
    Number(sort !== defaultSort)
  );
}

export interface RankedTrack {
  id?: string;
  name?: string;
}

export function popularityIndex(topTracks: RankedTrack[] | null | undefined) {
  const byId = new Map<string, number>();
  const byName = new Map<string, number>();
  (topTracks || []).forEach((track, index) => {
    if (track?.id && !byId.has(track.id)) byId.set(track.id, index + 1);
    const key = trackIdentityKey(track?.name);
    if (key && !byName.has(key)) byName.set(key, index + 1);
  });
  return (id: string | undefined, name: string | undefined): number | null => {
    if (id && byId.has(id)) return byId.get(id) ?? null;
    const key = trackIdentityKey(name);
    return key && byName.has(key) ? byName.get(key) ?? null : null;
  };
}

export interface GroupableTrack {
  releaseKey: string;
  trackNumber?: number | null;
}

export interface TrackGroup<T> {
  key: string;
  tracks: T[];
}

export function groupTracksByRelease<T extends GroupableTrack>(tracks: T[]): TrackGroup<T>[] {
  const groups: TrackGroup<T>[] = [];
  const index = new Map<string, TrackGroup<T>>();
  for (const track of tracks) {
    let group = index.get(track.releaseKey);
    if (!group) {
      group = { key: track.releaseKey, tracks: [] };
      index.set(track.releaseKey, group);
      groups.push(group);
    }
    group.tracks.push(track);
  }
  for (const group of groups) {
    group.tracks.sort((a, b) => (a.trackNumber ?? Number.MAX_SAFE_INTEGER) - (b.trackNumber ?? Number.MAX_SAFE_INTEGER));
  }
  return groups;
}

export function orderForGrouping<T extends GridEntry & GroupableTrack>(entries: T[], sort: ReleaseSort): T[] {
  const direction = sort === "oldest" ? 1 : -1;
  return [...entries].sort((a, b) => {
    const byDate = (releaseTime(a.releaseDate) - releaseTime(b.releaseDate)) * direction;
    if (byDate !== 0) return byDate;
    if (a.releaseKey !== b.releaseKey) return a.releaseKey.localeCompare(b.releaseKey);
    return (a.trackNumber ?? 0) - (b.trackNumber ?? 0);
  });
}

export function isGroupedView(filters: GridFilters, sort: ReleaseSort): boolean {
  return !filters.query.trim() && (sort === "newest" || sort === "oldest");
}
